import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import MicRecorder from "mic-recorder-to-mp3-fixed";

import { useParams } from 'react-router-dom';

import { makeStyles } from 'tss-react/mui';
import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MoodIcon from "@mui/icons-material/Mood";
import SendIcon from "@mui/icons-material/Send";
import CancelIcon from "@mui/icons-material/Cancel";
import ClearIcon from "@mui/icons-material/Clear";
import MicIcon from "@mui/icons-material/Mic";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircle";
import { FormControlLabel, Switch, useMediaQuery } from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import { isString, isEmpty, isObject, has } from "lodash";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";

const NOMBRES_CANAL = {
  whatsapp: "WhatsApp",
  whatsapp_cloud: "WhatsApp Cloud",
  facebook: "Messenger",
  instagram: "Instagram",
  sandbox: "Sandbox",
};
import useQuickMessages from "../../hooks/useQuickMessages";

import Compressor from 'compressorjs';
import LinearWithValueLabel from "./ProgressBarCustom";

import InternalMessagueImage from '../../assets/internal-message.gif';
import DefaultImage from '../../assets/default-image.gif';

import TemplateWindowPicker from "./TemplateWindowPicker";



const withWidth = () => (WrappedComponent) => (props) => {
  const isDesktop = useMediaQuery("(min-width:900px)");
  return <WrappedComponent {...props} width={isDesktop ? "md" : "xs"} />;
};

const isWidthUp = (breakpoint, width) => width === "md";



const Mp3Recorder = new MicRecorder({ bitRate: 128 });

const useStyles = makeStyles()((theme) => ({
  mainWrapper: {
    background: theme.palette.mode === "light" ? "#eee" : "#171b20",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  newMessageBox: {
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center",
    transition: "background-color 0.5s ease",
    backgroundColor: theme.palette.mode === "light" ? "#eee" : "#171b20",
  },
  newMessageBoxInternal: {
    backgroundColor: theme.palette.mode === "light" ? "#FFAC99" : "#5c4a3a",
  },

  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background: theme.palette.mode === "light" ? "#fff" : "#2a2f37",
    display: "flex",
    borderRadius: 20,
    flex: 1,
  },

  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
    color: theme.palette.text.primary,
    background: "transparent",
  },

  sendMessageIcons: {
    color: "grey",
  },

  uploadInput: {
    display: "none",
  },

  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.mode === "light" ? "#eee" : "#20252c",
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  mediaPanel: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    backgroundColor: theme.palette.mode === "light" ? "#f0f2f5" : "#1e2429",
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  mediaPanelHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
  },

  mediaPanelTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },

  mediaPreviewArea: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 180,
    maxHeight: 260,
    padding: "8px 12px",
    overflow: "hidden",
  },

  mediaPreviewImage: {
    maxWidth: "100%",
    maxHeight: 244,
    objectFit: "contain",
    borderRadius: 8,
  },

  mediaPreviewVideo: {
    maxWidth: "100%",
    maxHeight: 244,
    borderRadius: 8,
    backgroundColor: "#000",
  },

  mediaPreviewFile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: 24,
    minWidth: 220,
    borderRadius: 10,
    backgroundColor: theme.palette.mode === "light" ? "#fff" : "#2a3138",
    color: theme.palette.text.primary,
    textAlign: "center",
  },

  mediaPreviewFileName: {
    fontSize: 14,
    fontWeight: 600,
    wordBreak: "break-word",
    maxWidth: 320,
  },

  mediaPreviewFileMeta: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
  },

  mediaCaptionRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
  },

  mediaCaptionInput: {
    flex: 1,
    padding: "10px 14px",
    borderRadius: 22,
    border: "none",
    outline: "none",
    fontSize: 15,
    backgroundColor: theme.palette.mode === "light" ? "#fff" : "#2a3138",
    color: theme.palette.text.primary,
  },

  mediaSendBtn: {
    backgroundColor: "#00a884",
    color: "#fff",
    "&:hover": { backgroundColor: "#019175" },
  },

  mediaThumbStrip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px 12px",
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },

  mediaThumb: {
    position: "relative",
    flex: "0 0 auto",
    width: 54,
    height: 54,
    borderRadius: 8,
    overflow: "hidden",
    cursor: "pointer",
    border: `2px solid transparent`,
    backgroundColor: theme.palette.mode === "light" ? "#fff" : "#2a3138",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  mediaThumbActive: {
    borderColor: "#00a884",
  },

  mediaThumbImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  mediaThumbRemove: {
    position: "absolute",
    top: -6,
    right: -6,
    padding: 2,
    backgroundColor: theme.palette.mode === "light" ? "#fff" : "#1e2429",
    "&:hover": { backgroundColor: theme.palette.mode === "light" ? "#eee" : "#000" },
  },

  mediaAddThumb: {
    flex: "0 0 auto",
    width: 54,
    height: 54,
    borderRadius: 8,
    border: `2px dashed ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    backgroundColor: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { borderColor: "#00a884", color: "#00a884" },
  },

  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: "1px solid #e8e8e8",
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: "red",
  },

  sendAudioIcon: {
    color: "green",
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
}));

const EmojiOptions = (props) => {
  const { disabled, showEmoji, setShowEmoji, handleAddEmoji } = props;
  const { classes } = useStyles();
  return (
    <>
      <IconButton
        aria-label="emojiPicker"
        component="span"
        disabled={disabled}
        onClick={(e) => setShowEmoji((prevState) => !prevState)}
        size="large">
        <MoodIcon className={classes.sendMessageIcons} />
      </IconButton>
      {showEmoji ? (
        <div className={classes.emojiBox}>
          <Picker
            data={data}
            perLine={16}
            previewPosition="none"
            skinTonePosition="none"
            onEmojiSelect={handleAddEmoji}
          />
        </div>
      ) : null}
    </>
  );
};

const SignSwitch = (props) => {
  const { width, setSignMessage, signMessage } = props;
  if (isWidthUp("md", width)) {
    return (
      <FormControlLabel
        sx={{ marginRight: "7px", color: "text.secondary" }}
        label={i18n.t("messagesInput.signMessage")}
        labelPlacement="start"
        control={
          <Switch
            size="small"
            checked={signMessage}
            onChange={(e) => {
              setSignMessage(e.target.checked);
            }}
            name="showAllTickets"
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#25d366",
                "&:hover": {
                  backgroundColor: "rgba(37, 211, 102, 0.08)",
                },
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#25d366",
              },
            }}
          />
        }
      />
    );
  }
  return null;
};

const FileInput = (props) => {
  const { handleChangeMedias, disableOption } = props;
  const { classes } = useStyles();
  return (
    <>
      <input
        multiple
        type="file"
        id="upload-button"
        disabled={disableOption()}
        className={classes.uploadInput}
        onChange={handleChangeMedias}
      />
      <label htmlFor="upload-button">
        <IconButton
          aria-label="upload"
          component="span"
          disabled={disableOption()}
          size="large">
          <AttachFileIcon className={classes.sendMessageIcons} />
        </IconButton>
      </label>
    </>
  );
};

const ActionButtons = (props) => {
  const {
    inputMessage,
    loading,
    recording,
    ticketStatus,
    handleSendMessage,
    handleCancelAudio,
    handleUploadAudio,
    handleStartRecording,
  } = props;
  const { classes } = useStyles();
  if (inputMessage) {
    return (
      <IconButton
        aria-label="sendMessage"
        component="span"
        onClick={handleSendMessage}
        disabled={loading}
        size="large">
        <SendIcon className={classes.sendMessageIcons} />
      </IconButton>
    );
  } else if (recording) {
    return (
      <div className={classes.recorderWrapper}>
        <IconButton
          aria-label="cancelRecording"
          component="span"
          fontSize="large"
          disabled={loading}
          onClick={handleCancelAudio}
          size="large">
          <HighlightOffIcon className={classes.cancelAudioIcon} />
        </IconButton>
        {loading ? (
          <div>
            <CircularProgress className={classes.audioLoading} />
          </div>
        ) : (
          <RecordingTimer />
        )}
        <IconButton
          aria-label="sendRecordedAudio"
          component="span"
          onClick={handleUploadAudio}
          disabled={loading}
          size="large">
          <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
        </IconButton>
      </div>
    );
  } else {
    return (
      <IconButton
        aria-label="showRecorder"
        component="span"
        disabled={loading || (ticketStatus == "pending" || ticketStatus == "closed")}
        onClick={handleStartRecording}
        size="large">
        <MicIcon className={classes.sendMessageIcons} />
      </IconButton>
    );
  }
};

const CustomInput = (props) => {
  const {
    loading,
    inputRef,
    ticketStatus,
    inputMessage,
    setInputMessage,
    handleSendMessage,
    handleInputPaste,
    disableOption,
    ticketId
  } = props;


  const { classes } = useStyles();
  const [quickMessages, setQuickMessages] = useState([]);
  const [integrations, setIntegrations] = useState([]);

  const [options, setOptions] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);



  const { user } = useContext(AuthContext);

  const { list: listQuickMessages } = useQuickMessages();

  useEffect(() => {
    async function fetchData() {
      const companyId = localStorage.getItem("companyId");
      try {
        const messages = await listQuickMessages({ companyId, userId: user.id });
        const options = messages.map((m) => {
          let truncatedMessage = m.message;
          if (isString(truncatedMessage) && truncatedMessage.length > 35) {
            truncatedMessage = m.message.substring(0, 35) + "...";
          }

          return {
            value: m.message,
            label: `/${m.shortcode} - ${truncatedMessage}`,
            mediapath: `${process.env.REACT_APP_BACKEND_URL}/public/${m.mediapath}`,
          };
        });

        setQuickMessages(options);
      } catch (err) {
        console.error(err);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function fetchIntegrations() {
      const { data } = await api.get("/queueIntegration/", {
        params: { pageNumber: 1 },
      });

      if (data.count > 0) {
        const integrationOptions = data.queueIntegrations.map((e) => ({
          value: String(e.id),
          label: `@${e.name} - ${e.id}`,
          type: "integration",
        }));


        setIntegrations(integrationOptions);
      }
    }
    fetchIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (
      isString(inputMessage) &&
      !isEmpty(inputMessage) &&
      inputMessage.length > 0
    ) {
      const firstCharacter = inputMessage.charAt(0);

      switch (firstCharacter) {
        case "/":
          setPopupOpen(true);
          const filteredOptions = quickMessages.filter((m) => {
            const lowercaseLabel = m.label.toLowerCase();
            const lowercaseInput = inputMessage.toLowerCase();

            return (
              lowercaseLabel.includes(lowercaseInput) ||
              lowercaseLabel.split(' ').some(word => word.includes(lowercaseInput))
            );
          });

          setOptions(filteredOptions);
          break;

        case "@":
          setPopupOpen(true);

          const filteredIntegrations = integrations.filter((m) => {
            const lowercaseLabel = m.label.trim().toLowerCase();
            const lowercaseInput = inputMessage.trim().toLowerCase();
            return (
              lowercaseLabel.includes(lowercaseInput) ||
              lowercaseLabel.split(' ').some(word => word.includes(lowercaseInput))
            );
          });

          setOptions(filteredIntegrations);
          break;
        default:

          setPopupOpen(false);
          break;
      }


    } else {
      setPopupOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputMessage]);

  const onKeyDown = (e) => {
    if (loading || e.shiftKey) return;
    if (e.key === "Enter") {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const onPaste = (e) => {
    if (ticketStatus == "open" || ticketStatus == "favorite") {
      handleInputPaste(e);
    }

  };

  const renderPlaceholder = () => {
    if (ticketStatus == "open" || ticketStatus == "favorite") {
      return i18n.t("messagesInput.placeholderOpen");
    }
    return i18n.t("messagesInput.placeholderClosed");
  };

  const setInputRef = (input) => {
    if (input) {
      inputRef.current = input;
    }
  };

  const handleUploadMediaX = async (imageUrl) => {

    const formData = new FormData();
    formData.append("fromMe", true);
    const response = await fetch(imageUrl);

    const blob = await response.blob();
    const mediaFile = new File([blob], "media", { type: blob.type });


    if (mediaFile.type.startsWith("image/")) {
      await new Promise((resolve, reject) => {
        new Compressor(mediaFile, {
          quality: 0.7,
          success(compressedMedia) {
            formData.append("medias", compressedMedia);
            formData.append("body", compressedMedia.name);
            resolve();
          },
          error(err) {
            reject(err);
          },
        });
      });
    } else {
      formData.append("medias", mediaFile);
      formData.append("body", mediaFile.name);
    }

    await api.post(`/messages/${ticketId}`, formData);


  };


  const uploadImageFromUrl = async (imageUrl) => {
    handleUploadMediaX(imageUrl);
  };

  const handleOpenIntegration = async (idIntegration) => {
    const data = await api.put("/tickets-integration", { idIntegration, ticketId });
  }

  return (
    <div className={classes.messageInputWrapper}>
      <Autocomplete
        freeSolo
        open={popupOpen}
        id="grouped-demo"
        value={inputMessage}
        options={options}
        filterOptions={(opts) => opts}
        clearIcon={null}
        slotProps={{
          popper: { sx: { zIndex: 1300 } },
          listbox: {
            sx: {
              "& .MuiAutocomplete-option": {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
              },
            },
          },
        }}
        getOptionLabel={(option) => {
          if (isObject(option)) {
            return option.label;
          } else {
            return option;
          }
        }}
        onChange={(event, opt) => {

          if (isObject(opt) && has(opt, "value")) {
            setInputMessage(opt.value);

            if (opt.type === "integration") {
              handleOpenIntegration(opt.value);
              setInputMessage("");
            }


            if (opt.value && opt.mediapath != null && opt.mediapath != `${process.env.REACT_APP_BACKEND_URL}/public/null`) {
              const imageUrl = opt.mediapath;
              uploadImageFromUrl(imageUrl);

            }

            setTimeout(() => {
              inputRef.current.scrollTop = inputRef.current.scrollHeight;
            }, 200);
          }
        }}
        onInputChange={(event, opt, reason) => {
          if (reason === "input") {
            setInputMessage(event.target.value);
          }
        }}
        onPaste={onPaste}
        style={{ width: "100%" }}
        renderInput={(params) => {
          const { onKeyDown: autocompleteOnKeyDown, ref: muiInputRef, ...htmlInputRest } =
            params.slotProps.htmlInput || {};
          const { ref: anchorRef, ...inputSlotRest } = params.slotProps.input;
          return (
            <InputBase
              {...inputSlotRest}
              {...htmlInputRest}
              ref={anchorRef}
              id={params.id}
              disabled={disableOption()}
              inputRef={(node) => {
                setInputRef(node);
                if (typeof muiInputRef === "function") muiInputRef(node);
                else if (muiInputRef) muiInputRef.current = node;
              }}
              placeholder={renderPlaceholder()}
              multiline
              fullWidth
              className={classes.messageInput}
              maxRows={5}
              onKeyDown={(e) => {
                autocompleteOnKeyDown?.(e);
                if (!popupOpen) {
                  onKeyDown(e);
                }
              }}
            />
          );
        }}
      />
    </div>
  );
};

const MessageInputCustom = (props) => {
  const { ticketStatus, ticketId, whatsappId, templateRequired, contact, channel } = props;

  const [manualTemplate, setManualTemplate] = useState(false);

  const { classes, cx } = useStyles();

  const [medias, setMedias] = useState([]);
  const [mediaCaptions, setMediaCaptions] = useState([]);
  const [activeMediaIdx, setActiveMediaIdx] = useState(0);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [percentLoading, setPercentLoading] = useState(0);

  const [InternalMessague, setInternalMessague] = useState(false);

  const inputRef = useRef();
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);

  const companyId = localStorage.getItem("companyId");

  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [esperandoEnWeb, setEsperandoEnWeb] = useState(null);

  useEffect(() => {
    if (!ticketId) return undefined;

    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });
    let caducar = null;

    socket.on(`company-${companyId}-webchat-esperando`, (data) => {
      if (String(data?.ticketId) !== String(ticketId)) return;

      setEsperandoEnWeb(data.canalActual || null);

      clearTimeout(caducar);
      caducar = setTimeout(() => setEsperandoEnWeb(null), 30000);
    });

    return () => {
      clearTimeout(caducar);
      socket.disconnect();
    };
  }, [ticketId]);

  useEffect(() => {
    const previews = medias.map((f) => {
      const isImage = f.type?.startsWith("image/");
      const isVideo = f.type?.startsWith("video/");
      return {
        name: f.name,
        size: f.size,
        type: f.type,
        isImage,
        isVideo,
        url: isImage || isVideo ? URL.createObjectURL(f) : null,
      };
    });
    setMediaPreviews(previews);
    return () => previews.forEach((p) => p.url && URL.revokeObjectURL(p.url));
  }, [medias]);

  useEffect(() => {
    setActiveMediaIdx((idx) =>
      idx >= medias.length ? Math.max(0, medias.length - 1) : idx
    );
  }, [medias.length]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
    try {
      const draftKey = `pendingDraft-${ticketId}`;
      const draft = sessionStorage.getItem(draftKey);
      if (draft) {
        sessionStorage.removeItem(draftKey);
        setInputMessage(draft);
      }
    } catch (err) {}
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setMediaCaptions([]);
      setActiveMediaIdx(0);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  const isTypingRef = useRef(false);
  useEffect(() => {
    if (!ticketId) return undefined;

    if (inputMessage.trim() === "") {
      if (isTypingRef.current) {
        isTypingRef.current = false;
        api.post(`/tickets/${ticketId}/typing`, { typing: false }).catch(() => { });
      }
      return undefined;
    }

    if (!isTypingRef.current) {
      isTypingRef.current = true;
      api.post(`/tickets/${ticketId}/typing`, { typing: true }).catch(() => { });
    }

    const stopTypingTimeout = setTimeout(() => {
      isTypingRef.current = false;
      api.post(`/tickets/${ticketId}/typing`, { typing: false }).catch(() => { });
    }, 3000);

    return () => clearTimeout(stopTypingTimeout);
  }, [inputMessage, ticketId]);


  const handleAddEmoji = (e) => {
    let emoji = e.native;
    setInputMessage((prevState) => prevState + emoji);
  };

  const compressIfImage = (file) =>
    new Promise((resolve) => {
      if (!file?.type?.startsWith("image/")) return resolve(file);
      if (file.type === "image/gif" || file.type === "image/webp") {
        return resolve(file);
      }
      // eslint-disable-next-line no-new
      new Compressor(file, {
        quality: 0.7,
        success: (result) => resolve(result),
        error: () => resolve(file),
      });
    });

  const handleChangeMedias = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const selected = Array.from(e.target.files);
    setMedias((prev) => [...prev, ...selected]);
    setMediaCaptions((prev) => [...prev, ...selected.map(() => "")]);
    e.target.value = "";
  };

  const handleRemoveMedia = (idx) => {
    setMedias((prev) => prev.filter((_, i) => i !== idx));
    setMediaCaptions((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCancelMedias = () => {
    setMedias([]);
    setMediaCaptions([]);
    setActiveMediaIdx(0);
  };

  const handleCaptionChange = (value) => {
    setMediaCaptions((prev) => {
      const next = [...prev];
      next[activeMediaIdx] = value;
      return next;
    });
  };

  const handleInputPaste = (e) => {
    if (e.clipboardData.files && e.clipboardData.files.length > 0) {
      const pasted = Array.from(e.clipboardData.files);
      setMedias((prev) => [...prev, ...pasted]);
      setMediaCaptions((prev) => [...prev, ...pasted.map(() => "")]);
    }
  };

  const handleUploadMedia = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (medias.length === 0 || loading) return;
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("fromMe", true);

      const processed = await Promise.all(medias.map(compressIfImage));
      processed.forEach((file, idx) => {
        formData.append("medias", file, medias[idx].name);
      });
      formData.append("captions", JSON.stringify(mediaCaptions));

      await api.post(`/messages/${ticketId}`, formData, {
        onUploadProgress: (event) => {
          setPercentLoading(Math.round((event.loaded * 100) / event.total));
        },
      });

      handleCancelMedias();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
      setPercentLoading(0);
    }
  };



  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    setLoading(true);

    if (isTypingRef.current) {
      isTypingRef.current = false;
      api.post(`/tickets/${ticketId}/typing`, { typing: false }).catch(() => { });
    }

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      InternalMessague: InternalMessague,
      body: signMessage
        ? `*${user?.name}:*\n${inputMessage.trim()}`
        : inputMessage.trim(),
      quotedMsg: replyingMessage,
    };
    try {
      await api.post(`/messages/${ticketId}`, message);


    } catch (err) {
      toastError(err);
    }

    setInputMessage("");
    setShowEmoji(false);
    setLoading(false);
    setReplyingMessage(null);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await Mp3Recorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const [, blob] = await Mp3Recorder.stop().getMp3();
      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const filename = `audio-record-site-${new Date().getTime()}.mp3`;
      formData.append("medias", blob, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      await Mp3Recorder.stop().getMp3();
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleClick = () => {
    setInternalMessague((prev) => !prev);
  };

  const getImageUrl = () => {
    return InternalMessague ? DefaultImage : InternalMessagueImage;
  };




  const disableOption = () => {
    if (InternalMessague) return loading || recording;
    return loading || recording || (ticketStatus == "pending" || ticketStatus == "closed");
  };

  const renderReplyingMessage = (message) => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={cx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {message.body}
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || (ticketStatus == "pending" || ticketStatus == "closed")}
          onClick={() => setReplyingMessage(null)}
          size="large">
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  if (medias.length > 0) {
    const activePreview = mediaPreviews[activeMediaIdx];
    const activeName = medias[activeMediaIdx]?.name || "";
    const activeCaption = mediaCaptions[activeMediaIdx] || "";

    const formatSize = (bytes) => {
      if (bytes === undefined || bytes === null) return "";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
      <Paper elevation={0} square className={classes.mediaPanel}>
        <div className={classes.mediaPanelHeader}>
          <IconButton
            aria-label="cancel-upload"
            size="small"
            onClick={handleCancelMedias}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
          <span className={classes.mediaPanelTitle}>
            {medias.length === 1 ? "1 archivo" : `${medias.length} archivos`}
          </span>
        </div>

        <div className={classes.mediaPreviewArea}>
          {activePreview?.isImage ? (
            <img
              src={activePreview.url}
              alt={activeName}
              className={classes.mediaPreviewImage}
            />
          ) : activePreview?.isVideo ? (
            <video
              src={activePreview.url}
              className={classes.mediaPreviewVideo}
              controls
            />
          ) : (
            <div className={classes.mediaPreviewFile}>
              <InsertDriveFileOutlinedIcon
                style={{ fontSize: 56, color: "#00a884" }}
              />
              <span className={classes.mediaPreviewFileName}>{activeName}</span>
              <span className={classes.mediaPreviewFileMeta}>
                {[activeName.split(".").pop(), formatSize(activePreview?.size)]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
          )}
        </div>

        {loading && (
          <div style={{ padding: "0 12px" }}>
            <LinearWithValueLabel progress={percentLoading} />
          </div>
        )}

        <div className={classes.mediaCaptionRow}>
          <input
            className={classes.mediaCaptionInput}
            placeholder="Añade un comentario…"
            value={activeCaption}
            disabled={loading}
            onChange={(e) => handleCaptionChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleUploadMedia(e);
              }
            }}
          />
          <IconButton
            className={classes.mediaSendBtn}
            aria-label="send-upload"
            onClick={handleUploadMedia}
            disabled={loading}
            size="large"
          >
            <SendIcon />
          </IconButton>
        </div>

        <div className={classes.mediaThumbStrip}>
          {mediaPreviews.map((p, idx) => (
            <div
              key={idx}
              className={cx(classes.mediaThumb, {
                [classes.mediaThumbActive]: idx === activeMediaIdx,
              })}
              onClick={() => setActiveMediaIdx(idx)}
            >
              {p.isImage ? (
                <img
                  src={p.url}
                  alt={p.name}
                  className={classes.mediaThumbImage}
                />
              ) : p.isVideo ? (
                <PlayCircleOutlineIcon />
              ) : (
                <InsertDriveFileOutlinedIcon />
              )}
              {!loading && (
                <IconButton
                  className={classes.mediaThumbRemove}
                  size="small"
                  aria-label="remove-media"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveMedia(idx);
                  }}
                >
                  <HighlightOffIcon fontSize="small" />
                </IconButton>
              )}
            </div>
          ))}
          {!loading && (
            <>
              <input
                multiple
                type="file"
                id="upload-more-button"
                className={classes.uploadInput}
                onChange={handleChangeMedias}
              />
              <label htmlFor="upload-more-button">
                <div className={classes.mediaAddThumb} aria-label="add-more">
                  <AddIcon />
                </div>
              </label>
            </>
          )}
        </div>
      </Paper>
    );
  } else {
    return (
      <Paper square elevation={0} className={classes.mainWrapper}>
        {}
        {esperandoEnWeb && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              fontSize: 13,
              lineHeight: 1.35,
              color: "#7a6023",
              backgroundColor: "#fdf3e3",
              borderTop: "1px solid #e3cfa0",
            }}
          >
            <span>
              Este cliente tiene el <b>chat web</b> abierto ahora mismo, pero tu
              respuesta va a salir por{" "}
              <b>{NOMBRES_CANAL[esperandoEnWeb] || esperandoEnWeb}</b>. Ahí no la
              va a ver.
            </span>
          </div>
        )}
        {replyingMessage && renderReplyingMessage(replyingMessage)}
        <div className={cx(classes.newMessageBox, { [classes.newMessageBoxInternal]: InternalMessague })}>
          {}
          {(!(templateRequired || manualTemplate) || InternalMessague) && (
            <>
              <EmojiOptions
                disabled={disableOption()}
                handleAddEmoji={handleAddEmoji}
                showEmoji={showEmoji}
                setShowEmoji={setShowEmoji}
              />

              <FileInput
                disableOption={disableOption}
                handleChangeMedias={handleChangeMedias}
              />

              <button
                style={{ borderColor: 'transparent', backgroundColor: 'transparent' }}
                className="image-button"
                onClick={handleClick}
                title={InternalMessague ? "Volver a la respuesta" : "Escribir mensaje interno (nota del equipo)"}
              >
                <img src={getImageUrl()} alt="Mensaje interno" style={{ width: '25px', marginTop: '2px', }} />
              </button>
            </>
          )}

          {}
          {channel === "whatsapp_cloud" && !templateRequired && !manualTemplate && !InternalMessague && (
            <Tooltip
              title={
                manualTemplate
                  ? "Volver a escribir un mensaje normal"
                  : "Enviar una plantilla (úsalo si Meta rechaza por ventana de 24 h cerrada)"
              }
            >
              <IconButton
                size="small"
                onClick={() => setManualTemplate((prev) => !prev)}
                color={manualTemplate ? "primary" : "default"}
              >
                <DescriptionOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {}
          {(templateRequired || manualTemplate) && !InternalMessague ? (
            <TemplateWindowPicker
              ticketId={ticketId}
              whatsappId={whatsappId}
              contact={contact}
              onWriteInternal={() => {
                setManualTemplate(false);
                setInternalMessague(true);
              }}
              onExitTemplates={
                manualTemplate && !templateRequired
                  ? () => setManualTemplate(false)
                  : undefined
              }
            />
          ) : (
            <>
              <SignSwitch
                width={props.width}
                setSignMessage={setSignMessage}
                signMessage={signMessage}
              />

              <CustomInput
                loading={loading}
                inputRef={inputRef}
                ticketStatus={ticketStatus}
                inputMessage={inputMessage}
                setInputMessage={setInputMessage}
                handleSendMessage={handleSendMessage}
                handleInputPaste={handleInputPaste}
                disableOption={disableOption}
                ticketId={ticketId}
              />

              <ActionButtons
                inputMessage={inputMessage}
                loading={loading}
                recording={recording}
                ticketStatus={ticketStatus}
                handleSendMessage={handleSendMessage}
                handleCancelAudio={handleCancelAudio}
                handleUploadAudio={handleUploadAudio}
                handleStartRecording={handleStartRecording}
              />
            </>
          )}
        </div>
      </Paper>
    );
  }
};

export default withWidth()(MessageInputCustom);
